'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Home, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BreadcrumbProps {
  className?: string;
  itemClassName?: string;
}

type BreadcrumbItem = {
  label: string;
  href: string;
  icon?: boolean;
};

export function Breadcrumb({ className, itemClassName }: BreadcrumbProps) {
  const pathname = usePathname();

  // Generate breadcrumb items from pathname
  const segments = pathname.split('/').filter((seg) => seg && seg !== 'en');
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', href: '/', icon: true },
    ...segments.map((segment, index) => {
      const href = '/' + segments.slice(0, index + 1).join('/');
      const label = segment
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return { label, href };
    }),
  ];

  return (
    <nav className={cn('flex items-center space-x-1', className)} aria-label="Breadcrumb">
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;

        return (
          <div key={item.href} className="flex items-center">
            {index > 0 && (
              <ChevronRight className="w-4 h-4 text-muted mx-1 flex-shrink-0" aria-hidden="true" />
            )}
            <Link href={item.href}>
              <span
                className={cn(
                  'transition-colors duration-200',
                  isLast
                    ? 'text-muted font-medium'
                    : 'text-primary hover:text-primary-dark',
                  itemClassName
                )}
              >
                {item.icon ? (
                  <Home className="w-4 h-4" aria-label="Home" />
                ) : (
                  item.label
                )}
              </span>
            </Link>
          </div>
        );
      })}
    </nav>
  );
}
